import * as React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowRight, Lock, Play, UserCheck, Users } from 'lucide-react'
import { sb } from '@/lib/supabase'
import { useAuth } from '@/auth/auth-provider'
import { useQuery } from '@/hooks/use-query'
import { formatDate } from '@/lib/format'
import { areaResults, loadSelfAssessmentCatalog } from '@/lib/self-assessment'
import type { SelfAssessment, SelfAssessmentScore } from '@/lib/types'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { EmptyState, ErrorNote, Loading, Page } from '@/components/page'
import { SelfAssessmentResults } from '@/components/self-assessment-results'

/**
 * /self-assessment — a person's own SWEBOK self-assessment (0067).
 *
 * Everything on this page is the signed-in person's: the query filters on their
 * user id, and RLS would return nobody else's rows anyway — except to a DPL
 * coach, who reads the room from /self-assessment/team instead. The page says
 * plainly who can see the answers, because an honest self-rating depends on
 * believing that.
 */
export function SelfAssessmentPage() {
  const { currentTeam, session, isCoach } = useAuth()
  const teamId = currentTeam?.id
  const userId = session?.user?.id
  const navigate = useNavigate()
  const [starting, setStarting] = React.useState(false)

  const { data, loading, error } = useQuery(async () => {
    if (!teamId || !userId) return null
    const catalog = await loadSelfAssessmentCatalog()
    if (!catalog) return null
    const runs = await sb()
      .from('self_assessments')
      .select('*')
      .eq('team_id', teamId)
      .eq('user_id', userId)
      .eq('template_id', catalog.template.id)
      .order('started_at', { ascending: false })
    if (runs.error) throw runs.error
    const ids = (runs.data ?? []).map((r) => r.id)
    const scores = ids.length
      ? await sb().from('self_assessment_scores').select('*').in('assessment_id', ids)
      : { data: [], error: null }
    if (scores.error) throw scores.error
    return {
      catalog,
      runs: (runs.data ?? []) as SelfAssessment[],
      scores: (scores.data ?? []) as SelfAssessmentScore[],
    }
  }, [teamId, userId])

  async function start() {
    if (!teamId || !data) return
    setStarting(true)
    try {
      const { data: row, error: err } = await sb()
        .from('self_assessments')
        .insert({ team_id: teamId, template_id: data.catalog.template.id })
        .select()
        .single()
      if (err) throw err
      navigate(`/self-assessment/${row.id}`)
    } catch (err) {
      window.alert(err instanceof Error ? err.message : String(err))
      setStarting(false)
    }
  }

  if (!currentTeam) return null

  const open = data?.runs.find((r) => r.status === 'in_progress')
  const finished = (data?.runs ?? []).filter((r) => r.status === 'complete')
  const latest = finished[0]
  const previous = finished[1]
  const scoresFor = (id: string) => (data?.scores ?? []).filter((s) => s.assessment_id === id)
  const total = data?.catalog.criteria.length ?? 0

  return (
    <Page
      title="Self-assessment"
      description="Where you are across the SWEBOK areas, AI included, and what to work on next."
      actions={
        isCoach ? (
          <Button variant="outline" size="sm" asChild>
            <Link to="/self-assessment/team">
              <Users /> Team view
            </Link>
          </Button>
        ) : undefined
      }
    >
      {error && <ErrorNote error={error} />}
      <div className="mb-5 flex items-start gap-2 rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
        <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>
          Your answers are yours. You can see them, and so can your Don’t Panic Labs coach. Nobody
          else on your team can — including your team lead.
        </span>
      </div>

      {loading && !data ? (
        <Loading />
      ) : !data ? (
        <EmptyState icon={UserCheck} title="Not available" description="The self-assessment has not been set up yet." />
      ) : (
        <div className="space-y-6">
          {open ? (
            <Card>
              <CardContent className="flex flex-wrap items-center gap-3 py-4">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">You have one in progress</p>
                  <p className="text-xs text-muted-foreground">
                    Started {formatDate(open.started_at)} · {scoresFor(open.id).length} of {total} answered
                  </p>
                </div>
                <Button asChild>
                  <Link to={`/self-assessment/${open.id}`}>
                    Continue <ArrowRight />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="flex flex-wrap items-center gap-3 py-4">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">
                    {latest ? 'Take it again' : 'Take the self-assessment'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {total} questions across {data.catalog.categories.length} areas, about 15 minutes.
                    {latest && ' Your last result stays, so you can see what moved.'}
                  </p>
                </div>
                <Button onClick={() => void start()} disabled={starting}>
                  <Play /> Start
                </Button>
              </CardContent>
            </Card>
          )}

          {latest ? (
            <section className="space-y-3">
              <h2 className="text-base font-semibold">
                Your latest result{' '}
                <span className="text-sm font-normal text-muted-foreground">
                  {formatDate(latest.completed_at)}
                </span>
              </h2>
              <SelfAssessmentResults
                catalog={data.catalog}
                results={areaResults(data.catalog, scoresFor(latest.id))}
                previous={previous ? areaResults(data.catalog, scoresFor(previous.id)) : null}
                nowLabel={formatDate(latest.completed_at)}
                previousLabel={previous ? formatDate(previous.completed_at) : undefined}
              />
            </section>
          ) : (
            !open && (
              <p className="text-sm text-muted-foreground">
                Your results appear here once you finish.
              </p>
            )
          )}

          {finished.length > 0 && (
            <section className="space-y-2">
              <h2 className="text-sm font-semibold">History</h2>
              <Card>
                <CardContent className="divide-y p-0">
                  {finished.map((r) => (
                    <Link
                      key={r.id}
                      to={`/self-assessment/${r.id}`}
                      className="flex items-center justify-between px-4 py-2.5 text-sm hover:bg-accent"
                    >
                      <span>{formatDate(r.completed_at)}</span>
                      <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    </Link>
                  ))}
                </CardContent>
              </Card>
            </section>
          )}
        </div>
      )}
    </Page>
  )
}
